import React, { useContext } from "react";

import LightPoll from "@/containers/Poll/templates/light/LightPoll";
import {
  getPollSympathizersCount,
  getPollTotalVotes,
  GlobalContext,
  PollConsumer,
  PollContextProps,
  PollProvider,
} from "@/contexts";
import {
  InteractionLayout,
  Poll,
  PollResultsContainer,
  PollVote,
  Slide,
} from "@/service/Model";

import BildTvAppPoll from "./templates/bildTvApp/BildTvAppPoll";
import ClassicPoll from "./templates/classic/ClassicPoll";

export type PollContainerProps = {
  interaction: Poll;
  interactionResults: PollResultsContainer;
};

export type PollProps = {
  votes: PollVote[];
  selection: PollVote[];
  votedSlideIds: string[];
  completed: boolean;
  expired: boolean;
  stoppedAtSlide: number;
  totalVoteCount: number;
  userVoteCount: number;
} & PollContainerProps;

const PollContainer: React.FC<PollContainerProps> = (
  props: PollContainerProps
): React.JSX.Element => {
  const globalContext = useContext(GlobalContext);
  const { interaction, interactionResults } = props;

  const renderTemplate = (pollContext: PollContextProps): React.JSX.Element => {
    const { votes } = pollContext;
    const votedSlideIds = interaction.slides
      .filter((slide: Slide) =>
        votes.find((vote: PollVote) => vote.slideId === slide.id)
      )
      .map((slide: Slide) => slide.id);

    const pollCompleted = votedSlideIds.length === interaction.slides.length;

    const expired = !!(interaction.endDate && interaction.endDate < Date.now());

    const getSliderPosition = (): number => {
      if (interaction.secretPoll && expired) {
        return 0;
      }

      // Keep in mind the poll configuration can be changed at each time in the admin ui
      let position = 0;
      if (votedSlideIds.length) {
        if (interaction.intro) {
          position++;
        }

        // Show summary only if there are multiple slides
        if (interaction.slides.length > 1) {
          position += votedSlideIds.length;
        }
      }
      return position;
    };

    const totalVoteCount = getPollTotalVotes(interactionResults.results);
    const userVoteCount = pollContext.votes
      ? getPollSympathizersCount(interactionResults.results, pollContext.votes)
      : 1;

    const pollProps: PollProps = {
      votes: pollContext.votes,
      selection: pollContext.selection,
      votedSlideIds: votedSlideIds,
      completed: pollCompleted,
      expired,
      stoppedAtSlide: getSliderPosition(),
      totalVoteCount,
      userVoteCount,
      ...props,
    };

    if (interaction.layout === InteractionLayout.Fullscreen) {
      return <BildTvAppPoll {...pollProps} />;
    } else if (interaction.layout === InteractionLayout.Light) {
      return <LightPoll {...pollProps} />;
    } else {
      return <ClassicPoll {...pollProps} />;
    }
  };

  return (
    <PollProvider
      interaction={interaction}
      preview={!!globalContext.query.preview}
    >
      <PollConsumer>
        {(pollContext: PollContextProps) => renderTemplate(pollContext)}
      </PollConsumer>
    </PollProvider>
  );
};

export default PollContainer;
export { PollContainer };
